import React from 'react'
import { useNavigate } from 'react-router-dom'
import { StatCard } from '@/design-system/components/StatCard'
import { Card, CardBody, CardHeader } from '@/design-system/components/Card'
import { Button } from '@/design-system/components/Button'
import { Badge } from '@/design-system/components/Badge'
import { useAdminStats } from './hooks/useAdmin'

interface ActivityEvent {
  id: string
  type: 'photographer' | 'mission' | 'alert' | 'payout'
  message: string
  time: string
}

const RECENT_ACTIVITY: ActivityEvent[] = [
  { id: '1', type: 'photographer', message: 'Ny Zoomer registrerad: Sara Nilsson (Malmö)', time: '2 min sedan' },
  { id: '2', type: 'mission', message: 'Uppdrag slutfört: Parkinspektion Haga — 15 bilder', time: '18 min sedan' },
  { id: '3', type: 'alert', message: 'Utbetalningsbegäran: Jonas Eriksson — 10 000 SEK', time: '45 min sedan' },
  { id: '4', type: 'photographer', message: 'Ny Zoomer registrerad: Erik Svensson (Västerås)', time: '1 h sedan' },
  { id: '5', type: 'mission', message: 'Nytt uppdrag skapat: Fastighetsinspektion Östermalm', time: '2 h sedan' },
  { id: '6', type: 'alert', message: '5 bilder väntar på granskning i uppdrag M-001', time: '3 h sedan' },
]

const typeIcon: Record<ActivityEvent['type'], string> = {
  photographer: '👤',
  mission: '📍',
  alert: '⚠️',
  payout: '💸',
}

const typeBadgeVariant: Record<ActivityEvent['type'], 'active' | 'complete' | 'pending' | 'urgent'> = {
  photographer: 'active',
  mission: 'complete',
  alert: 'urgent',
  payout: 'pending',
}

export function AdminDashboard() {
  const { data: stats, isLoading } = useAdminStats()
  const navigate = useNavigate()

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(v)

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Överblick</h1>
        <p className="text-sm text-text-secondary mt-1">Wavult Admin — quiXzoom plattform</p>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Totalt Zoomers"
          value={isLoading ? '—' : stats!.total_photographers}
          delta="12 nya denna månad"
          deltaPositive
          loading={isLoading}
        />
        <StatCard
          label="Aktiva uppdrag"
          value={isLoading ? '—' : stats!.active_missions}
          loading={isLoading}
          live
        />
        <StatCard
          label="Bilder denna vecka"
          value={isLoading ? '—' : new Intl.NumberFormat('sv-SE').format(stats?.images_this_week ?? 0)}
          delta="↑ 18% vs förra veckan"
          deltaPositive
          loading={isLoading}
        />
        <StatCard
          label="Intäkter MTD"
          value={isLoading ? '—' : formatCurrency(stats?.revenue_mtd ?? 0)}
          delta="På spår mot budget"
          deltaPositive
          loading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent activity */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold text-text-primary">Senaste aktivitet</h2>
            </CardHeader>
            <CardBody className="p-0">
              <ul className="divide-y divide-surface-border">
                {RECENT_ACTIVITY.map(event => (
                  <li key={event.id} className="flex items-start gap-3 px-5 py-3.5">
                    <span className="text-base mt-0.5">{typeIcon[event.type]}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary">{event.message}</p>
                      <p className="text-xs text-text-muted mt-0.5">{event.time}</p>
                    </div>
                    <Badge variant={typeBadgeVariant[event.type]}>
                      {event.type === 'photographer' ? 'Zoomer'
                        : event.type === 'mission' ? 'Uppdrag'
                        : event.type === 'alert' ? 'Alert'
                        : 'Utbetalning'}
                    </Badge>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </div>

        {/* Quick links */}
        <div>
          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold text-text-primary">Snabblänkar</h2>
            </CardHeader>
            <CardBody className="space-y-3">
              <Button
                variant="secondary"
                fullWidth
                icon={<span>🖼</span>}
                onClick={() => navigate('/admin/images')}
              >
                Granska bilder
              </Button>
              <Button
                variant="secondary"
                fullWidth
                icon={<span>📍</span>}
                onClick={() => navigate('/admin/missions')}
              >
                Hantera uppdrag
              </Button>
              <Button
                variant="secondary"
                fullWidth
                icon={<span>👤</span>}
                onClick={() => navigate('/admin/photographers')}
              >
                Se Zoomers
              </Button>
              <Button
                variant="secondary"
                fullWidth
                icon={<span>💸</span>}
                onClick={() => navigate('/admin/payouts')}
              >
                Utbetalningar
              </Button>

              {/* Pending badge info */}
              <div className="pt-2 border-t border-surface-border space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">Bilder att granska</span>
                  <Badge variant="urgent">2 väntande</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">Utbetalningar att godkänna</span>
                  <Badge variant="pending">3 väntande</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">Nya Zoomers (pending)</span>
                  <Badge variant="default">1 ny</Badge>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  )
}
